import {
  FilterType,
  FilterValueTypes,
  LeafFilterType,
  NodeFilterType,
  TreeFilterType,
} from './types'
import { ILeaf, INode } from 'shared/types/tree'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { traverseTree } from '../../utility/TreeHelpers'
import { allContentForFilterTest } from './utility'

export function getFilteredResults<T = INode | ILeaf>(
  filter: TreeFilterType,
  tree: INode,
): T[] {
  switch (filter.filterType) {
    case FilterType.leaf:
      return [...getFilteredLeaves(filter, tree)] as T[]
    case FilterType.node:
      return [...getFilteredNodes(filter, tree)] as T[]
    default:
      return []
  }
}

function* getFilteredNodes(
  filter: NodeFilterType,
  tree: INode,
): IterableIterator<INode> {
  const toCheck = getFilteredDescendants(filter, tree)
  for (const n of toCheck) {
    if (!isFilteredInByType(filter, n)) continue
    if (!isFilteredInByBodyValue(filter, n.body)) continue
    if (isFilteredOutByDescendants(filter, n)) continue
    if (isFilteredOutByContent(filter, n)) continue
    yield n
  }
}

function* getFilteredLeaves(filter: LeafFilterType, tree: INode) {
  const toCheck = getFilteredDescendants(filter, tree)

  for (const n of toCheck) {
    const siblings = getFilteredSiblingLeaves(n, filter)
    if (!siblings) continue
    for (const l of siblings) {
      if (!isFilteredInByType(filter, l, siblings)) continue
      if (!isFilteredInByBodyValue(filter, l.body)) continue
      if (isFilteredOutByContent(filter, l)) continue
      yield l
    }
  }
}

function getFilteredDescendants({ descendantOf }: TreeFilterType, tree: INode) {
  if (!descendantOf) return traverseTree(tree)
  const { directDescendant, filter } = descendantOf

  const filtered = getFilteredNodes(filter, tree)

  if (directDescendant) {
    return Array.from(filtered).reduce<INode[]>((acc, n) => {
      return [...acc, ...n.nodes]
    }, [])
  }

  return traverseTree(...filtered)
}

function isFilteredInByType<T extends { type: string }>(
  filter: TreeFilterType,
  item: T,
  siblings: T[] = [],
): boolean {
  switch (true) {
    case !filter.typeSelections?.types?.length:
    case filter.typeSelections?.filterType === 'including' &&
      filter.typeSelections.types?.includes(item.type as NodeType | LeafTypes):
    case filter.typeSelections?.filterType === 'excluding' &&
      !filter.typeSelections.types?.includes(item.type as NodeType | LeafTypes):
      return true
    case filter.typeSelections?.filterType === 'duplicateSibling': {
      const [theType] = filter.typeSelections.types
      if (item.type !== theType) return false
      return !!siblings
        .slice(0, siblings.indexOf(item))
        .find((sibling) => sibling.type === theType)
    }
    default:
      return false
  }
}

function isFilteredInByBodyValue(
  filter: TreeFilterType,
  body?: Record<string, unknown>,
) {
  if (!filter.body) return true
  // Try to find a body filter where the condition fails
  // if found return false
  return !Object.entries(filter.body).find(([key, { type, value }]) => {
    const toTest = body ?? {}
    switch (type) {
      case 'equal': {
        if (!value.length) return false
        return isExistingValueFiltered(toTest[key], value)
      }
      case 'notEqual': {
        if (!value.length) return false
        return !isExistingValueFiltered(toTest[key], value)
      }
      default: {
        return false
      }
    }
  })
}

function getFilteredSiblingLeaves({ leaves }: INode, filter: LeafFilterType) {
  if (!leaves) return leaves

  const tests: Array<(leaf: ILeaf, index: number) => boolean> = []

  const siblingValueFilter = filter.body
    ? Object.entries(filter.body).find(([, value]) => value.type === 'self')
    : undefined

  if (siblingValueFilter) {
    const [bodyKey] = siblingValueFilter
    const bodyTest = (leaf: ILeaf, index: number) => {
      const currentValue = leaf.body?.[bodyKey]
      if (currentValue == null) return false

      return leaves.slice(0, index).some((sibling) => {
        const testValue = sibling.body?.[bodyKey]
        return currentValue === testValue
      })
    }

    tests.push(bodyTest)
  }

  // If no filters are active, return the original leaves
  if (tests.length === 0) return leaves

  // Apply all active tests (AND semantics: must pass every test)
  return leaves.filter((leaf, index) =>
    tests.every((test) => test(leaf, index)),
  )
}

function isExistingValueFiltered(
  existingValue: unknown,
  checkSet: FilterValueTypes[],
) {
  if (!isFilterValueType(existingValue)) return true
  if (checkSet.includes(existingValue)) return false
  if (typeof existingValue !== 'string') return true
  const regexTests: RegExp[] = checkSet.filter(isRegexp)
  return !regexTests.find((r) => r.test(existingValue))
}

function isFilterValueType(value: unknown): value is FilterValueTypes {
  if (['string', 'number', 'boolean'].includes(typeof value)) return true
  return false
}

function isRegexp(obj: unknown): obj is RegExp {
  return obj instanceof RegExp
}

function isFilteredOutByDescendants(
  { withDescendant }: TreeFilterType,
  node: INode,
) {
  if (!withDescendant?.filter?.length) return false
  const descendantTrees = node.nodes.reduce<INode[]>((acc, n) => {
    return [
      ...acc,
      {
        ...n,
        nodes: withDescendant.directDescendant ? [] : n.nodes,
      },
    ]
  }, [])
  // This is used as a proxy tree to filter child nodes
  const fakeParent = {
    type: '',
    body: {},
    leaves: node.leaves,
    nodes: descendantTrees,
  } as INode
  if (withDescendant.mustIncludeAll) {
    return !withDescendant.filter.every((f) =>
      getFilteredResults(f, fakeParent).some((n: unknown) => !!n),
    )
  } else {
    return !withDescendant.filter.some((f) =>
      getFilteredResults(f, fakeParent).some((n: unknown) => !!n),
    )
  }
}

function isFilteredOutByContent(
  { withContent }: TreeFilterType,
  item: INode | ILeaf,
) {
  if (!withContent || !Object.keys(withContent).length) return false
  const toTest = allContentForFilterTest(item)
  const { ALL_OR = false, ...rest } = withContent

  const violates = (
    keyword: string,
    cfg: { filterType: 'including' | 'excluding'; caseInsensitive?: boolean },
  ) => {
    const haystack = cfg.caseInsensitive ? toTest.toLowerCase() : toTest
    const needle = cfg.caseInsensitive ? keyword.toLowerCase() : keyword
    const contains = haystack.includes(needle)

    // A rule is “violated” when:
    //   • an excluding keyword **is present**, or
    //   • an including keyword **is absent**
    return contains === (cfg.filterType === 'excluding')
  }

  const violations = Object.keys(rest).map((key) => violates(key, rest[key]))
  return ALL_OR ? violations.every(Boolean) : violations.some(Boolean)
}
